import i18n from 'i18next';
import type { i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'de', 'es'] as const;

// Temporary product-level lock. fr/de/es bundles exist but are not ready to
// ship — flip this to false to restore browser/storage-driven language
// selection (and the footer selector will reappear automatically).
export const LANGUAGE_LOCKED_TO_EN = true;

export function resolveInitialLanguage(
  language: string | undefined,
): (typeof SUPPORTED_LANGUAGES)[number] {
  if (LANGUAGE_LOCKED_TO_EN) return 'en';

  const normalized = language?.toLowerCase().split('-')[0];

  if (
    normalized &&
    SUPPORTED_LANGUAGES.includes(normalized as (typeof SUPPORTED_LANGUAGES)[number])
  ) {
    return normalized as (typeof SUPPORTED_LANGUAGES)[number];
  }

  return 'en';
}

// Reads locale directly from localStorage to avoid circular import with preferences store.
// Key must match PREFERENCES_STORAGE_KEY in stores/preferences.ts.
function getStoredLocale(): string | undefined {
  try {
    const raw = globalThis.localStorage?.getItem('ab.preferences');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    const locale = parsed?.state?.locale;
    return typeof locale === 'string' ? locale : undefined;
  } catch {
    return undefined;
  }
}

export const i18nInstance: I18nInstance = i18n.createInstance();

i18nInstance
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) => import(`../locales/${language}/${namespace}.json`),
    ),
  )
  .init({
    lng: resolveInitialLanguage(getStoredLocale() ?? globalThis.navigator?.language),
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  })
  .catch(console.error);
